/**
 * Черновик пошаговой анкеты (SurveyFlowSheet: промежуточная ОС и финальная NPS).
 *
 * Отзыв из итоговой анкеты: «если свернуть приложение, уже введённые ответы
 * стираются; в стартовой черновик сохраняется, а тут нет». Telegram выгружает
 * свёрнутый WebView, и ответы, жившие только в стейте компонента, пропадали —
 * а финальная анкета на ~15 минут с текстовыми ответами. Держим черновик в
 * localStorage, как стартовая анкета (`tfa_onboarding_draft:<userId>`): только
 * на этом устройстве, после успешной отправки удаляем.
 */

export type SurveyDraftKind = 'FEEDBACK' | 'NPS'

export interface SurveyDraft {
  /** Индекс вопроса, на котором остановились (0…total-1). */
  step: number
  answers: Record<string, unknown>
}

interface StoredDraft extends SurveyDraft {
  /** Когда записали — старый черновик (анкета давно закрыта) не воскрешаем. */
  at: number
}

/** Анкета открыта неделю-другую; черновик старше 60 дней — мусор. */
const TTL_MS = 60 * 24 * 60 * 60 * 1000

export function surveyDraftKey(kind: SurveyDraftKind, userId: string | number): string {
  return `tfa_survey_draft:${kind}:${userId}`
}

/**
 * Читает черновик под текущий список вопросов: ответы на исчезнувшие вопросы
 * отбрасываем, шаг зажимаем в границы. Пустой черновик = его нет.
 */
export function loadSurveyDraft(key: string, questionIds: string[], now = Date.now()): SurveyDraft | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredDraft> | null
    if (!parsed || typeof parsed !== 'object') return null
    if (typeof parsed.at === 'number' && now - parsed.at > TTL_MS) {
      localStorage.removeItem(key)
      return null
    }
    const known = new Set(questionIds)
    const answers: Record<string, unknown> = {}
    for (const [id, value] of Object.entries(parsed.answers ?? {})) {
      if (known.has(id) && value !== undefined && value !== null && value !== '') answers[id] = value
    }
    if (Object.keys(answers).length === 0 || questionIds.length === 0) return null
    const rawStep = typeof parsed.step === 'number' && Number.isFinite(parsed.step) ? Math.floor(parsed.step) : 0
    const step = Math.min(Math.max(rawStep, 0), questionIds.length - 1)
    return { step, answers }
  } catch {
    // битый JSON / нет доступа к storage — начинаем с чистого листа
    return null
  }
}

/** Пишет черновик; без ответов — удаляет (нечего восстанавливать). */
export function saveSurveyDraft(key: string, draft: SurveyDraft, now = Date.now()): void {
  try {
    const hasAnswers = Object.values(draft.answers).some((v) => v !== undefined && v !== null && v !== '')
    if (!hasAnswers) {
      localStorage.removeItem(key)
      return
    }
    const stored: StoredDraft = { step: draft.step, answers: draft.answers, at: now }
    localStorage.setItem(key, JSON.stringify(stored))
  } catch {
    // quota / private mode — игнорируем
  }
}

export function clearSurveyDraft(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // ignore
  }
}
